import type { Edge, Node } from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';

import type { ErdSchemaResponse, ErdSchemaResponseErdColumnSchema } from '~/server_shared/openapi/data-contracts';

export type TableNodeType = Node<
  {
    label: string;
    columns: ErdSchemaResponseErdColumnSchema[];
    isConnected?: boolean;
    isDimmed?: boolean;
  },
  'table'
>;

// Node dimensions are computed (not measured) so elk can lay out before first render.
// Keep in sync with the Tailwind heights in TableNode.
export const TABLE_NODE_WIDTH = 280;
export const TABLE_HEADER_HEIGHT = 32;
export const TABLE_ROW_HEIGHT = 24;

export const getTableNodeHeight = (columnsCount: number): number =>
  TABLE_HEADER_HEIGHT + columnsCount * TABLE_ROW_HEIGHT;

export const buildGraph = (schema: ErdSchemaResponse): { nodes: TableNodeType[]; edges: Edge[] } => {
  const nodes: TableNodeType[] = schema.tables.map((table) => ({
    id: table.name,
    type: 'table',
    position: { x: 0, y: 0 },
    data: { label: table.name, columns: table.columns },
  }));

  const tableNames = new Set(schema.tables.map((table) => table.name));
  const edges: Edge[] = schema.relationships
    .filter((rel) => tableNames.has(rel.from.table) && tableNames.has(rel.to.table))
    .map((rel) => ({
      id: `${rel.from.table}.${rel.from.column}->${rel.to.table}.${rel.to.column}`,
      source: rel.from.table,
      sourceHandle: rel.from.column,
      target: rel.to.table,
      targetHandle: rel.to.column,
      type: 'smoothstep',
    }));

  return { nodes, edges };
};

const elk = new ELK();

const ELK_LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.spacing.nodeNodeBetweenLayers': '120',
  'elk.spacing.nodeNode': '60',
  'elk.separateConnectedComponents': 'true',
  'elk.spacing.componentComponent': '100',
};

export const layoutGraph = async (nodes: TableNodeType[], edges: Edge[]): Promise<TableNodeType[]> => {
  const layouted = await elk.layout({
    id: 'root',
    layoutOptions: ELK_LAYOUT_OPTIONS,
    children: nodes.map((node) => ({
      id: node.id,
      width: TABLE_NODE_WIDTH,
      height: getTableNodeHeight(node.data.columns.length),
    })),
    edges: edges.map((edge) => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
  });

  const positions = new Map((layouted.children ?? []).map((child) => [child.id, { x: child.x ?? 0, y: child.y ?? 0 }]));
  return nodes.map((node) => ({ ...node, position: positions.get(node.id) ?? node.position }));
};
